import { Label } from "components/label"
import { CollapsibleSection } from "../../../../../../components/collapsibleSection"
import { Popover, Transition } from "@headlessui/react"
import { ChromePicker, ColorResult } from "react-color"
import { Controller, UseFormReturn } from "react-hook-form"
import { IEditOrgForm } from "interfaces"
import { HttpError } from "@refinedev/core"
import Dropzone from "components/dropzone"
import { XIcon } from "@heroicons/react/outline"

export const OwnerAccountSection = (props: UseFormReturn<IEditOrgForm, HttpError, IEditOrgForm>) => {
  const { control, formState: { errors } } = props;

  return (
    <CollapsibleSection label="Owner account information">
      <div className="divide-y divide-gray-100">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="sm:col-span-3">
              <Label 
                id="cover_banner" 
                name="Cover banner (1400x300)"
                hint="The image will be used and displayed as the cover banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported"
              />
              <Controller
                control={control}
                name="cover_banner"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="cover_banner"
                      validator={{
                        width: 1400,
                        height: 300
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["cover_banner"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["cover_banner"].message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="organization_banner" 
                name="Organization banner (1440x600)"
                hint="The image will be used and displayed on the Organizer Information card in the Organizer List page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported"
              />
              <Controller
                control={control}
                name="organization_banner"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="organization_banner"
                      validator={{
                        width: 1440,
                        height: 600
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["organization_banner"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["organization_banner"].message}
                  </span>
                </p> )
              : null}
            </div>

            <div className="sm:col-span-3">
              <Label 
                id="rectangular_promotion_banner" 
                name="Rectangular promotion banner (950x300)"
                hint="The image will be used and displayed as the promotion banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
              />
              <Controller
                control={control}
                name="rectangular_promotion_banner"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="rectangular_promotion_banner"
                      validator={{
                        width: 950,
                        height: 300
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["rectangular_promotion_banner"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["rectangular_promotion_banner"]?.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-3">
              <Label 
                id="square_promotion_banner" 
                name="Square promotion banner (300x300)"
                hint="The image will be used and displayed as the promotion banner in the Organization Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
              />
              <Controller
                control={control}
                name="square_promotion_banner"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="square_promotion_banner"
                      validator={{
                        width: 300,
                        height: 300
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["square_promotion_banner"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["square_promotion_banner"].message}
                  </span>
                </p> )
              : null}
            </div>

            <div className="sm:col-span-2">
              <Label 
                id="organization_logo" 
                name="Organization logo (600x600)"
                hint="The image will be used and displayed as a round image in the Organizer Details page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported"
              />
              <Controller
                control={control}
                name="organization_logo"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="organization_logo"
                      validator={{
                        width: 600,
                        height: 600
                      }}
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["organization_logo"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["organization_logo"]?.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-2">
              <Label 
                id="brand_logo" 
                name="Brand logo"
                hint="The logo will be replaced for the ActiUp logo position when accessing the Organizer Detail page on the actiup.net website. Only PNG, JPG, and JPEG file formats are supported."
              />
              <Controller
                control={control}
                name="brand_logo"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Dropzone
                      id="brand_logo"
                      onChange={(imageUrl: string) => onChange(imageUrl)}
                      value={value}
                    />
                  )
                }}
              />
              {errors["brand_logo"] ? ( 
                <p className="error-text">
                  <span className="font-medium">
                    {errors["brand_logo"]?.message}
                  </span>
                </p> )
              : null}
            </div>
            <div className="sm:col-span-2 relative">
              <Label 
                id="brand_color" 
                name="Brand color"
                hint="The selected color code will replace the header color of ActiUp when accessing the Organizer Detail page on the actiup.net website."
              />
              <Controller
                control={control}
                name="brand_color"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <Popover className="relative">
                      {value ? (
                        <button 
                          type="button"
                          className="absolute top-1/2 right-4 transform -translate-y-1/2 z-1" 
                          onClick={() => onChange("")}
                        >
                          <span className="sr-only">Clear color</span>
                          <XIcon className="w-4 h-4 text-gray-900" aria-hidden="true" />
                        </button>
                      ) : <></>}
                      <Popover.Button className="input border flex items-center gap-x-3 border-gray-300 cursor-pointer outline-none uppercase">
                        <div 
                          className="w-6 h-6 rounded-md border-1 border-solid border-black" 
                          style={{backgroundColor: value}}
                        /> 
                        {value}
                      </Popover.Button>
                      <Transition
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                      >
                        <Popover.Panel className="w-fit sm:absolute">
                          <div className="w-fit">
                            <ChromePicker
                              disableAlpha
                              onChange={(color: ColorResult) => onChange(color.hex)}
                              color={value} className="mt-2"
                            />
                          </div>
                        </Popover.Panel>
                      </Transition>
                    </Popover>
                  )
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  )
}